import React, { FC } from 'react';
import { Stack } from '@mui/material';
import Modal from 'molecules/Modal';
import { ModalContent } from 'molecules/Modal/Modal.styles';
import {
  StyledRow,
  StyledRowText,
  StyledTextButton,
} from 'organisms/ProjectDialogs/CloudOpsProject.styles';
import { UserState } from 'store/user/userSlice';
import { getUserRoleLabel } from './RolesTable';
import dayjs from 'dayjs';
import { palette } from 'utils/styles/variables';
import { GOOGLE_SALES_REP, SUPER_ADMIN } from 'utils/constants/roles';
import { useBaoSelector } from 'utils/hooks/redux';

export const UserModal: FC<{
  open: boolean;
  user?: Partial<UserState>;
  onClose: () => void;
  onDelete: () => void;
  onRoleChange: () => void;
  onResendInvite: () => void;
  onChangeName: () => void;
}> = ({
  open,
  user,
  onClose,
  onDelete,
  onResendInvite,
  onRoleChange,
  onChangeName,
}) => {
  const account = useBaoSelector((state) => state.user.value);

  const isSuperAdmin = account.userType === SUPER_ADMIN;

  const isActionsEnabled = isSuperAdmin && account.userId !== user?.id;
  const isResendInviteEnabled = () => {
    if (!isActionsEnabled) return false;
    if (!user?.lastResentInvite) return true;
    return dayjs().isAfter(dayjs(user.lastResentInvite), 'day');
  };

  if (user?.userType === GOOGLE_SALES_REP) {
    return (
      <Modal open={open} maxWidth="xs" title="User details" onClose={onClose}>
        <ModalContent sx={{ padding: '0 16px 24px !important' }}>
          <Stack gap="8px">
            <StyledRow>
              <StyledRowText>
                Name:{' '}
                <StyledRowText $bold>
                  {user?.name || ''} {user?.surname || ''}
                </StyledRowText>
              </StyledRowText>
              {isActionsEnabled && (
                <StyledTextButton variant="text" onClick={onChangeName}>
                  Change name
                </StyledTextButton>
              )}
            </StyledRow>
            <StyledRow>
              <StyledRowText>
                Role:{' '}
                <StyledRowText $bold>
                  {getUserRoleLabel(user?.userType as string) || ''}
                </StyledRowText>
              </StyledRowText>
              {/* {isActionsEnabled && (
                <StyledTextButton
                  variant="text"
                  onClick={onRoleChange}
                >
                  Change role
                </StyledTextButton>
              )} */}
            </StyledRow>
            {user?.lastLoggedTime ? (
              <StyledRow>
                <StyledRowText>
                  Last logged in at:{' '}
                  <StyledRowText $bold>
                    {dayjs(user.lastLoggedTime).format('MMM DD, YYYY')}
                  </StyledRowText>
                </StyledRowText>
              </StyledRow>
            ) : (
              <StyledRow>
                <StyledRowText $color={palette.warning[500]}>
                  Invitation has not accepted yet
                </StyledRowText>
                {isResendInviteEnabled() ? (
                  <StyledTextButton variant="text" onClick={onResendInvite}>
                    Resend invitation
                  </StyledTextButton>
                ) : (
                  <StyledRowText $color={palette.warning[500]}>
                    Invitation resent
                  </StyledRowText>
                )}
              </StyledRow>
            )}
            <StyledRow>
              <StyledRowText>
                Created at:{' '}
                {user?.createdAt ? (
                  <StyledRowText $bold>
                    {dayjs(user.createdAt).format('MMM DD, YYYY')}
                  </StyledRowText>
                ) : (
                  ''
                )}
              </StyledRowText>
            </StyledRow>
            <StyledRow>
              <StyledRowText>
                Created by:{' '}
                {user?.createdBy ? (
                  <StyledRowText $bold>{user.createdBy}</StyledRowText>
                ) : (
                  ''
                )}
              </StyledRowText>
            </StyledRow>
            <StyledRow>
              <StyledRowText>
                Modified at:{' '}
                {user?.updatedAt ? (
                  <StyledRowText $bold>
                    {dayjs(user.updatedAt).format('MMM DD, YYYY')}
                  </StyledRowText>
                ) : (
                  ''
                )}
              </StyledRowText>
            </StyledRow>
            <StyledRow>
              <StyledRowText>
                Modified by:{' '}
                {user?.updatedBy ? (
                  <StyledRowText $bold>{user.updatedBy}</StyledRowText>
                ) : (
                  ''
                )}
              </StyledRowText>
            </StyledRow>
            <StyledRow>
              <StyledRowText />
              {isActionsEnabled && !user?.dealCount && (
                <StyledTextButton variant="text" onClick={onDelete}>
                  Delete user
                </StyledTextButton>
              )}
            </StyledRow>
          </Stack>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal open={open} maxWidth="xs" title="User details" onClose={onClose}>
      <ModalContent sx={{ padding: '0 16px 24px !important' }}>
        <Stack gap="8px">
          <StyledRow>
            <StyledRowText>
              Name:{' '}
              <StyledRowText $bold>
                {user?.name || ''} {user?.surname || ''}
              </StyledRowText>
            </StyledRowText>
            {isActionsEnabled && (
              <StyledTextButton variant="text" onClick={onDelete}>
                Delete user
              </StyledTextButton>
            )}
          </StyledRow>
          <StyledRow>
            <StyledRowText>
              Email: <StyledRowText $bold>{user?.email || ''}</StyledRowText>
            </StyledRowText>
          </StyledRow>
          <StyledRow>
            <StyledRowText>
              Role:{' '}
              <StyledRowText $bold>
                {getUserRoleLabel(user?.userType as string) || ''}
              </StyledRowText>
            </StyledRowText>
            {isActionsEnabled && (
              <StyledTextButton variant="text" onClick={onRoleChange}>
                Change role
              </StyledTextButton>
            )}
          </StyledRow>
          {user?.lastLoggedTime ? (
            <StyledRow>
              <StyledRowText>
                Last logged in at:{' '}
                <StyledRowText $bold>
                  {dayjs(user.lastLoggedTime).format('MMM DD, YYYY')}
                </StyledRowText>
              </StyledRowText>
            </StyledRow>
          ) : (
            <StyledRow>
              <StyledRowText $color={palette.warning[500]}>
                Invitation has not accepted yet
              </StyledRowText>
              {isResendInviteEnabled() ? (
                <StyledTextButton variant="text" onClick={onResendInvite}>
                  Resend invitation
                </StyledTextButton>
              ) : (
                <StyledRowText $color={palette.warning[500]}>
                  Invitation resent
                </StyledRowText>
              )}
            </StyledRow>
          )}
          <StyledRow>
            <StyledRowText>
              Created at:{' '}
              {user?.createdAt ? (
                <StyledRowText $bold>
                  {dayjs(user.createdAt).format('MMM DD, YYYY')}
                </StyledRowText>
              ) : (
                ''
              )}
            </StyledRowText>
          </StyledRow>
          <StyledRow>
            <StyledRowText>
              Created by:{' '}
              {user?.createdBy ? (
                <StyledRowText $bold>{user.createdBy}</StyledRowText>
              ) : (
                ''
              )}
            </StyledRowText>
          </StyledRow>
          <StyledRow>
            <StyledRowText>
              Modified at:{' '}
              {user?.updatedAt ? (
                <StyledRowText $bold>
                  {dayjs(user.updatedAt).format('MMM DD, YYYY')}
                </StyledRowText>
              ) : (
                ''
              )}
            </StyledRowText>
          </StyledRow>
          <StyledRow>
            <StyledRowText>
              Modified by:{' '}
              {user?.updatedBy ? (
                <StyledRowText $bold>{user.updatedBy}</StyledRowText>
              ) : (
                ''
              )}
            </StyledRowText>
          </StyledRow>
        </Stack>
      </ModalContent>
    </Modal>
  );
};
